import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';

import { getCategoryIDFromUrl, getCategoryNameFromUrl } from 'components/upb_viewer/UPBViewer';
import SupportTicketsCreateModal from 'components/upb_viewer/SupportTicketsCreateModal';
import BetaBreadcrumb from 'components/upb_viewer_beta/BetaBreadcrumb';
import BetaPagination from 'components/upb_viewer_beta/BetaPagination';
import BetaPaneSelect from 'components/upb_viewer_beta/BetaPaneSelect';
import BetaProductGrid from 'components/upb_viewer_beta/BetaProductGrid';
import BetaSearchLoading from 'components/upb_viewer_beta/BetaSearchLoading';
import useUPBBetaProducts from 'components/upb_viewer_beta/hooks/useUPBBetaProducts';
import { addReferrerToUrl } from 'lib/utils/navigationUtils';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import {
  BG_1,
  BORDER_2,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_MEDIUM,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  SHADOW_XS,
} from 'components/upb_viewer_beta/css/betaTokens';

// Sort options match the design mockup. (Annualized Units is intentionally
// absent: no reliable units source — quantities mix UOM cases/eaches/pounds.)
const SORT_OPTIONS = [
  { value: 'annualized_gmv', label: 'Annualized GMV' },
  { value: 'market_share', label: 'Market Share' },
  { value: 'name', label: 'Name (A→Z)' },
  { value: 'dp_count', label: 'DPs' },
  { value: 'attribute_score', label: 'Attribute Score' },
];

/**
 * BetaProductsPage — beta-styled product listing for a leaf category or search.
 *
 * Layout matches the design mockup (UPB Viewer - Products.html):
 *   - Breadcrumb (Home › All Categories › L1 … › L4) with level tags
 *   - Controls row: results count + SKU type select + sort select
 *   - Product card grid
 *   - Pagination
 *
 * SKU type is hoisted to the container so the same selection drives the funnel
 * and the category page. Sort is a UI-only placeholder until the `/search`
 * endpoint supports a sort param (tracked as a backend follow-up).
 */
function BetaProductsPage({
  urlParams,
  searchQuery,
  baseURL,
  skuType,
  skuTypeOptions,
  onSkuTypeChange,
  categoryTotalGmv,
  onNavigate,
}) {
  const { l1, l2, l3, l4 } = urlParams;
  const history = useHistory();
  const location = useLocation();

  const l1Id = getCategoryIDFromUrl(l1) || undefined;
  const l2Id = getCategoryIDFromUrl(l2) || undefined;
  const l3Id = getCategoryIDFromUrl(l3) || undefined;
  const l4Id = getCategoryIDFromUrl(l4) || undefined;

  const trimmedSearch = (searchQuery || '').trim();
  const isSearching = trimmedSearch.length > 0;

  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('annualized_gmv');
  // "Report a data issue" ticket modal, opened from above the controls row.
  const [showIssueModal, setShowIssueModal] = useState(false);
  const page = useMemo(() => Math.max(currentPage - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE, [page]);

  // Reset paging whenever the active filter set changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, l1Id, l2Id, l3Id, l4Id]);

  const { data: { data: products = [], count = 0 } = {}, loading } = useUPBBetaProducts({
    offset,
    limit: DEFAULT_UPB_PAGE_SIZE,
    searchQuery,
    l1CategoryId: l1Id,
    l2CategoryId: l2Id,
    l3CategoryId: l3Id,
    l4CategoryId: l4Id,
  });

  // Annotate each product with a derived per-L4 market share. Snowflake's
  // PERCENTAGE column on the L5 row is a tiny global share (and frequently
  // zero), so compute it ourselves: product GMV / L4 total GMV. The L4 total
  // is the KPI strip's annualized_gmv, passed in by the container.
  const productsWithShare = useMemo(() => {
    const denom = Number(categoryTotalGmv) || 0;
    return products.map(p => {
      const gmv = Number(p.invoice_and_ordering_gmv_percentage) || 0;
      const share = denom > 0 ? (gmv / denom) * 100 : null;
      return { ...p, _market_share: share };
    });
  }, [products, categoryTotalGmv]);

  // Client-side filter + sort applied to the current page of results.
  //   - SKU type 'active' keeps products with at least one DP carrying them.
  //   - SKU type 'gmv' keeps products with any L5 GMV (% > 0).
  //   - Sort uses the merged payload fields. NB: this only re-orders / filters
  //     the *current* page; true cross-page server-side sort/filter would
  //     require backend changes to /search (tracked as a follow-up).
  const visibleProducts = useMemo(() => {
    // Search results arrive already ordered by relevance (semantic ranking) from
    // the backend — preserve that order and apply no SKU-type filter or GMV sort
    // (those are category-browse controls only).
    if (isSearching) return productsWithShare;
    let list = productsWithShare;
    // Filter only when the field is explicitly present and zero. Missing
    // fields mean the backend hasn't been extended yet (with_vendor_aggregates
    // or with_l5_gmv_percentages still off), so treat them as "include"
    // rather than dropping the entire result set.
    if (skuType === 'active') {
      list = list.filter(p => p.dp_count == null || p.dp_count > 0);
    } else if (skuType === 'gmv') {
      list = list.filter(
        p =>
          p.invoice_and_ordering_gmv_percentage == null ||
          p.invoice_and_ordering_gmv_percentage > 0,
      );
    }
    const sorted = [...list];
    const num = v => (v == null ? -Infinity : Number(v));
    const cmp = {
      annualized_gmv: (a, b) =>
        num(b.invoice_and_ordering_gmv_percentage) - num(a.invoice_and_ordering_gmv_percentage),
      market_share: (a, b) => num(b._market_share) - num(a._market_share),
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      dp_count: (a, b) => num(b.dp_count) - num(a.dp_count),
      attribute_score: (a, b) => num(b.fsa_score) - num(a.fsa_score),
    };
    if (cmp[sortKey]) sorted.sort(cmp[sortKey]);
    return sorted;
  }, [productsWithShare, skuType, sortKey, isSearching]);

  const totalPages = Math.max(Math.ceil(count / DEFAULT_UPB_PAGE_SIZE), 1);
  const startIdx = count === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + DEFAULT_UPB_PAGE_SIZE, count);
  const filteredOut = products.length - visibleProducts.length;

  // Breadcrumb — search bypasses the hierarchy. Levels carry an L-tag pill.
  // Single root crumb (mirrors BetaCategoryPage) — Home + All Categories used
  // to share a URL, which Devin's review flagged as confusing.
  let crumbs;
  if (isSearching) {
    crumbs = [
      { label: 'All Categories', url: `/${baseURL}` },
      { label: `Search: "${trimmedSearch}"` },
    ];
  } else {
    crumbs = [{ label: 'All Categories', url: `/${baseURL}` }];
    const segs = [l1, l2, l3, l4];
    segs.forEach((seg, idx) => {
      if (!seg) return;
      const isLast = segs.slice(idx + 1).every(s => !s);
      const url = isLast
        ? undefined
        : `/${[baseURL, ...segs.slice(0, idx + 1).filter(Boolean)].join('/')}`;
      crumbs.push({
        label: getCategoryNameFromUrl(seg) || 'Category',
        url,
        tag: `L${idx + 1}`,
      });
    });
  }

  const handleProductClick = product => {
    const base = `/product-details/${product.fsa_id || product.id}/global`;
    history.push(addReferrerToUrl(base, `${location.pathname}${location.search}`));
  };

  return (
    <div className={css(styles.page)}>
      <BetaBreadcrumb crumbs={crumbs} onNavigate={onNavigate} />

      {/* Sits directly above the Sort box (the controls row is right-aligned,
          so this lines up over it) — per reviewer feedback asking for a data
          issue report affordance on the product listing screens. */}
      <div className={css(styles.reportRow)}>
        <Button
          type="button"
          variant="link"
          className={css(styles.reportIssue)}
          onClick={() => setShowIssueModal(true)}
        >
          <span aria-hidden>⚠</span>
          Report a data issue
        </Button>
      </div>

      <div className={css(styles.controls)}>
        <span className={css(styles.resultsCount)}>
          {isSearching && loading
            ? 'Searching…'
            : count === 0
            ? 'No items'
            : `${startIdx}–${endIdx} of ${count} items${
                filteredOut > 0 ? ` (${visibleProducts.length} on this page)` : ''
              }`}
        </span>
        {/* SKU-type filter + sort are category-browse controls. In search mode the
            results are relevance-ranked by the backend, so we hide them. */}
        {!isSearching && onSkuTypeChange && skuTypeOptions && skuTypeOptions.length > 0 && (
          <BetaPaneSelect
            caption="SKU type"
            options={skuTypeOptions}
            value={skuType}
            onChange={onSkuTypeChange}
            menuWidth={240}
            minWidth={170}
            compact
          />
        )}
        {!isSearching && (
          <BetaPaneSelect
            caption="Sort"
            options={SORT_OPTIONS}
            value={sortKey}
            onChange={setSortKey}
            menuWidth={240}
            minWidth={200}
            compact
          />
        )}
      </div>

      {isSearching && loading ? (
        <BetaSearchLoading />
      ) : (
        <BetaProductGrid
          products={visibleProducts}
          loading={loading}
          onProductClick={handleProductClick}
        />
      )}

      {!(isSearching && loading) && totalPages > 1 && (
        <BetaPagination
          totalPages={totalPages}
          currentPageNumber={page}
          onPageChange={newPage => setCurrentPage(newPage + 1)}
        />
      )}

      {showIssueModal && (
        <SupportTicketsCreateModal
          onHide={() => setShowIssueModal(false)}
          refetch={() => {}}
          product={null}
          typeOfProduct={null}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  reportRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: -4,
  },

  // Same pill treatment as the MFR viewer's "Report an issue" button so the
  // two beta viewers match.
  reportIssue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: FG_2,
    padding: '7px 14px',
    borderRadius: 9999,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    boxShadow: SHADOW_XS,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ':hover': {
      color: PRIMARY_HOVER,
      borderColor: PRIMARY_COLOR,
      textDecoration: 'none',
    },
  },

  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  resultsCount: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
    marginRight: 'auto',
  },
});

BetaProductsPage.propTypes = {
  urlParams: PropTypes.shape({
    l1: PropTypes.string,
    l2: PropTypes.string,
    l3: PropTypes.string,
    l4: PropTypes.string,
  }).isRequired,
  searchQuery: PropTypes.string,
  baseURL: PropTypes.string,
  skuType: PropTypes.string,
  skuTypeOptions: PropTypes.arrayOf(PropTypes.object),
  onSkuTypeChange: PropTypes.func,
  categoryTotalGmv: PropTypes.number,
  onNavigate: PropTypes.func,
};

BetaProductsPage.defaultProps = {
  searchQuery: '',
  baseURL: 'upb-viewer-beta',
  skuType: 'active',
  skuTypeOptions: null,
  onSkuTypeChange: null,
  categoryTotalGmv: 0,
  onNavigate: null,
};

export default BetaProductsPage;
